import Modal, { ModalStateProps } from '../../../ui/Modal'
import UploadField from '../../../ui/form/UploadField'
import { useSearchTableState } from '../../../components'
import { DragEvent, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { ProjectContext } from '../../../contexts'
import api from '../../../api'
import './ImageGalleryModal.css'
import { Image, SearchResult } from '../../../types'
import Alert from '../../../ui/Alert'
import { Tabs } from '../../../ui'
import Heading from '../../../ui/Heading'
import TextInput from '../../../ui/form/TextInput'
import FormWrapper from '../../../ui/form/FormWrapper'
import { SearchIcon } from '../../../ui/icons'
import { useDebounceControl } from '../../../hooks'
import { useTranslation } from 'react-i18next'
import CursorPagination from '../../../ui/Pagination'

export type ImageUpload = Pick<Image, 'url' | 'alt' | 'name'>

interface OnInsert {
    onInsert: (image: ImageUpload) => void
}

type ImageGalleryProps = ModalStateProps & OnInsert

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

interface GalleryProps extends OnInsert {
    upload?: FileList
    isUploading: boolean
    error?: string
    onUpload: (files: FileList) => void
    searchState: ReturnType<typeof useSearchTableState<SearchResult<Image>>>
}

const Gallery = ({ onInsert, upload, isUploading, error, onUpload, searchState }: GalleryProps) => {
    const { t } = useTranslation()
    const { params, setParams, results } = searchState
    const [search, setSearch] = useDebounceControl(params.q ?? '', q => setParams({ ...params, q, cursor: undefined, cursor_direction: 'next' }))

    return (
        <div className="image-gallery">
            <p>{t('image_upload')}</p>
            {error && <Alert variant="error" title={error} />}
            <UploadField
                value={upload}
                onChange={onUpload}
                name="file"
                label={t('file')}
                isUploading={isUploading}
                accept={'image/*'}
                required />

            <Heading
                size="h4"
                title={t('library')}
                actions={
                    <TextInput
                        name="image_search"
                        size="small"
                        value={search}
                        placeholder={t('search_images')}
                        onChange={setSearch}
                        hideLabel={true}
                        icon={<SearchIcon />}
                    />
                }
            />

            {results && <>
                <div className="images">
                    {results.results.map(image => {
                        const isProcessing = image.status === 'pending' || image.status === 'processing'
                        return <div
                            className={`image${isProcessing ? ' is-processing' : ''}`}
                            key={`image-${image.id}`}
                            onClick={() => onInsert?.(image)}>
                            <img src={image.thumbnail_url ?? image.url} alt={image.alt ?? image.name} />
                            {isProcessing && <span className="image-status">{t('processing')}</span>}
                        </div>
                    })}
                </div>
                <CursorPagination
                    nextCursor={results.nextCursor}
                    prevCursor={results.prevCursor}
                    onPrev={cursor => setParams({ ...params, cursor, cursor_direction: 'prev' })}
                    onNext={cursor => setParams({ ...params, cursor, cursor_direction: 'next' })}
                />
            </>}
        </div>
    )
}

interface URLParams {
    url: string
}

const URL = ({ onInsert }: OnInsert) => {
    const { t } = useTranslation()
    const handleSubmit = async (params: URLParams) => {
        onInsert({
            url: params.url,
            alt: '',
            name: params.url.split('/').pop() ?? 'Image',
        })
    }

    return (
        <div className="image-gallery" onSubmit={e => e.stopPropagation()}>
            <FormWrapper<URLParams>
                onSubmit={handleSubmit}
                submitLabel={t('submit')}>
                {form => <>
                    <p>{t('image_url')}</p>
                    <TextInput.Field form={form} name="url" label={t('image_url_label')} />
                </>}
            </FormWrapper>
        </div>
    )
}

export default function ImageGalleryModal({ open, onClose, onInsert }: ImageGalleryProps) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const searchState = useSearchTableState<SearchResult<Image>>(useCallback(async params => await api.images.search(project.id, params), [project]))
    const { results, reload } = searchState
    const [upload, setUpload] = useState<FileList | undefined>()
    const [error, setError] = useState<string | undefined>()
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const dragCounterRef = useRef(0)

    const hasProcessing = results?.results.some(i => i.status === 'pending' || i.status === 'processing') ?? false
    const reloadRef = useRef(reload)
    reloadRef.current = reload
    useEffect(() => {
        if (!hasProcessing) return
        const id = setInterval(() => { reloadRef.current().catch(() => {}) }, 2500)
        return () => clearInterval(id)
    }, [hasProcessing])

    useEffect(() => {
        if (!open) {
            dragCounterRef.current = 0
            setIsDragging(false)
            setError(undefined)
        }
    }, [open])

    const uploadImage = useCallback(async (files: FileList) => {
        setError(undefined)
        const file = files?.[0]
        if (!file) return
        if (!file.type.startsWith('image/')) {
            setError(t('image_upload_invalid_type'))
            return
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            setError(t('image_upload_too_large', { max: '10 MB' }))
            return
        }
        setUpload(files)
        try {
            await api.images.create(project.id, file)
            await reload()
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? t('image_upload_error'))
        }
        setUpload(undefined)
    }, [project, reload, t])

    const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer.types ?? []).includes('Files')

    const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
        if (!hasFiles(event)) return
        event.preventDefault()
        event.stopPropagation()
        dragCounterRef.current += 1
        setIsDragging(true)
    }

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (!hasFiles(event)) return
        event.preventDefault()
        event.stopPropagation()
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1)
        if (dragCounterRef.current === 0) setIsDragging(false)
    }

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        if (!hasFiles(event)) return
        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = 'copy'
    }

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        if (!hasFiles(event)) return
        event.preventDefault()
        event.stopPropagation()
        dragCounterRef.current = 0
        setIsDragging(false)
        const files = event.dataTransfer.files
        if (!files || files.length === 0) return
        setSelectedIndex(0)
        void uploadImage(files)
    }

    const tabs = [
        {
            key: 'gallery',
            label: t('gallery'),
            children: (
                <Gallery
                    onInsert={onInsert}
                    upload={upload}
                    isUploading={upload !== undefined}
                    error={error}
                    onUpload={uploadImage}
                    searchState={searchState}
                />
            ),
        },
        {
            key: 'url',
            label: t('url'),
            children: <URL onInsert={onInsert} />,
        },
    ]

    return (
        <Modal
            title={t('images')}
            open={open}
            onClose={onClose}
            size="large">
            <div
                className={`image-gallery-dropzone${isDragging ? ' is-dragging' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}>
                <Tabs tabs={tabs} selectedIndex={selectedIndex} onChange={setSelectedIndex} />
                {isDragging && (
                    <div className="image-gallery-drop-overlay">
                        <span>{t('image_drop_to_upload')}</span>
                    </div>
                )}
            </div>
        </Modal>
    )
}
